import React from "react";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import type { FormContext } from "@features/port-security/components/SecurityClearence.types.ts";
import {
  uaeStates,
  type PortSecurityFormData,
} from "@features/port-security/schema/portSecurityFormSchema";
import { useFormattedReference } from "@hooks/useFormattedReference.ts";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi.ts";
import { t } from "i18next";
import { FormProvider } from "react-hook-form";
import { useOutletContext } from "react-router-dom";

type VehicleType = {
  [key: string]: string;
};

const ParticularsOfVehicle: React.FC = () => {
  const { formData, updateFormData } = useOutletContext<FormContext>();
  const { options: vehicleTypes } = useFormattedReference<VehicleType>(
    REFERENCE_DATA_TYPES.VEHICLE_TYPES,
    "bff-gatepass",
  );
  const formattedVehicleTypes = vehicleTypes.map((vehicleType) => ({
    value: vehicleType[Object.keys(vehicleType)[0]],
    label: vehicleType[Object.keys(vehicleType)[0]],
  }));
  const stateOptions = uaeStates.map((state) => ({
    label: state.state,
    value: state.state,
  }));

  return (
    <FormProvider {...formData}>
      <FormSection title={t("security-clearance.particulars-of-vehicle")}>
        <FormItem<PortSecurityFormData, "typeOfVehicle">
          name="typeOfVehicle"
          type="autocomplete"
          options={formattedVehicleTypes}
          label={t("security-clearance.type-of-vehicle")}
          testId="security-typeOfVehicle"
          onFieldChange={updateFormData}
        />
        <FormItem<PortSecurityFormData, "licenseNumber">
          name="licenseNumber"
          type="text"
          maxChars={200}
          label={t("security-clearance.driving-license-number")}
          testId="security-licenseNumber"
          onFieldChange={updateFormData}
        />
        <FormItem<PortSecurityFormData, "placeOfIssue">
          name="placeOfIssue"
          type="autocomplete"
          options={stateOptions}
          label={t("security-clearance.place-of-issue")}
          testId="security-placeOfIssue"
          onFieldChange={updateFormData}
        />
        <FormItem<PortSecurityFormData, "licensedBy">
          name="licensedBy"
          type="text"
          maxChars={200}
          label={t("security-clearance.licensed-by")}
          testId="security-licensedBy"
          onFieldChange={updateFormData}
        />
        <FormItem<PortSecurityFormData, "dateOfIssue">
          name="dateOfIssue"
          type="date-picker"
          label={t("security-clearance.date-of-issue")}
          testId="security-dateOfIssue"
          onFieldChange={updateFormData}
          timePicker={false}
          dateConstraint="not-future"
        />
        <FormItem<PortSecurityFormData, "plateNumber">
          name="plateNumber"
          type="text"
          maxChars={200}
          label={t("security-clearance.plate-number")}
          testId="security-plateNumber"
          onFieldChange={updateFormData}
        />
        <FormItem<PortSecurityFormData, "dateOfLicenseExpiry">
          name="dateOfLicenseExpiry"
          type="date-picker"
          label={t("security-clearance.license-expiry-date")}
          testId="security-dateOfLicenseExpiry"
          onFieldChange={updateFormData}
          timePicker={false}
          dateConstraint="future"
        />
        <FormItem<PortSecurityFormData, "plateCode">
          name="plateCode"
          type="text"
          maxChars={200}
          label={t("security-clearance.plate-code")}
          testId="security-plateCode"
          onFieldChange={updateFormData}
        />
      </FormSection>
    </FormProvider>
  );
};

export default ParticularsOfVehicle;
